import React, { Dispatch, SetStateAction, createContext, useMemo } from 'react';
import { TFunction } from 'i18next';
import { useWasteChecksheetDetail } from '../hooks/useWasteChecksheetDetail';
import { ExtendedGetItemsResponse } from '../interfaces/ExtendedGetItemsResponse';
import { ItemWasteChecksheetData, SaveStatus } from '../interfaces/ItemWasteChecksheetData';
import { ItemPreview } from '../interfaces/ItemsPreview';
import { Blocker } from 'react-router-dom';
import { Category } from '../interfaces/Category';

export interface WasteChecksheetDetailValues {
  t: TFunction<'createStockCount', undefined>; // Assuming 'TFunction' is a type for translation functions.
  isLoading: boolean; // Boolean value for loading state.
  isDirty: boolean; // Boolean value for tracking if there are unsaved changes.
  showSubmitAlert: boolean; // Boolean value to show/hide the submit alert.
  itemsData: ExtendedGetItemsResponse; // Array of items.
  itemsByCategory: Category[]; // Object with category names as keys and arrays of items.
  isArchived: boolean; // Boolean value to check if the data is archived.
  submittedBy: string | null; // String value for the user who submitted, or null if not available.
  submittedDate: string | undefined; // Date as a string (you might want to use Date type if needed).
  loadData: () => void; // Function to load data.
  updateCategoryItems: (itemId: number | undefined, fromStatus: SaveStatus | undefined, toStatus: SaveStatus) => void; // Function to update items for a category.
  handleLeavePageModalCancel: () => void; // Function to handle canceling leaving the page.
  handleLeavePageModalClose: () => void; // Function to close the leave page modal.
  handleLeavePageModalConfirm: () => void; // Function to confirm leaving the page.
  handleItemFocus: (itemId: number) => void; // Function to handle focusing on an item.
  handleSaveConfirmModal: () => void; // Function to handle confirming save.
  handleSubmitButton: () => void; // Function to handle the submit button click.
  handleSubmitCancelModal: () => void; // Function to handle canceling submit modal.
  handleReset: () => void; // Function to handle reset action.
  isSaving: boolean; // Boolean value to check if it's in the saving state.
  isSubmitting: boolean; // Boolean value to check if it's in the submitting state.
  showSavedAlert: boolean; // Boolean value to show saved alert.
  showErrorSavedAlert: boolean; // Boolean value to show error saved alert.
  showErrorSubmitAlert: boolean; // Boolean value to show error submit alert.
  openSubmitModal: boolean; // Function to open the submit modal.
  setShowSavedAlert: (value: boolean) => void; // Function to set showSavedAlert state.
  setShowErrorSavedAlert: (value: boolean) => void; // Function to set showErrorSavedAlert state.
  setShowErrorSubmitAlert: (value: boolean) => void; // Function to set showErrorSubmitAlert state.
  printTableContainer: () => void;
  hasPermissionToWrite: boolean;
  isMobile: boolean;
  isSaveSubmitEnabled: () => boolean;
  isEditMode: boolean;
  handleQuantityChange: (categoryIndex: number, itemIndex: number, field: keyof ItemWasteChecksheetData, value: string, isOnBlur: boolean) => void;
  isPrinting: boolean;
  handleConfirmSubmitModal: () => void;
  itemsPreviewOnSubmitModal: ItemPreview[];
  modifiedItems: Set<number>;
  blocker: Blocker;
  setShowSubmitAlert: Dispatch<SetStateAction<boolean>>;
}

export const WasteChecksheetDetailContext = createContext<null | WasteChecksheetDetailValues>(null);

interface ProviderProps {
  children: React.ReactNode;
}

export const WasteChecksheetDetailProvider = ({ children }: ProviderProps) => {

  const {
    t,
    isLoading,
    isDirty,
    showSubmitAlert,
    itemsData,
    itemsByCategory,
    isArchived,
    submittedBy,
    submittedDate,
    loadData,
    updateCategoryItems,
    handleLeavePageModalCancel,
    handleLeavePageModalClose,
    handleLeavePageModalConfirm,
    handleItemFocus,
    handleSaveConfirmModal,
    handleSubmitButton,
    handleSubmitCancelModal,
    handleReset,
    isSaving,
    isSubmitting,
    showSavedAlert,
    showErrorSavedAlert,
    showErrorSubmitAlert,
    openSubmitModal,
    setShowSavedAlert,
    setShowErrorSavedAlert,
    setShowErrorSubmitAlert,
    printTableContainer,
    hasPermissionToWrite,
    isMobile,
    isSaveSubmitEnabled,
    isEditMode,
    handleQuantityChange,
    isPrinting,
    handleConfirmSubmitModal,
    itemsPreviewOnSubmitModal,
    modifiedItems,
    blocker,
    setShowSubmitAlert,
  } = useWasteChecksheetDetail(); // Ensure you're using the correct hook for these values

  // Passed values must match the types defined in the interface.
  const passedValue: WasteChecksheetDetailValues = {
    t,
    isLoading,
    isDirty,
    showSubmitAlert,
    itemsData,
    itemsByCategory,
    isArchived,
    submittedBy,
    submittedDate,
    loadData,
    updateCategoryItems,
    handleLeavePageModalCancel,
    handleLeavePageModalClose,
    handleLeavePageModalConfirm,
    handleItemFocus,
    handleSaveConfirmModal,
    handleSubmitButton,
    handleSubmitCancelModal,
    handleReset,
    isSaving,
    isSubmitting,
    showSavedAlert,
    showErrorSavedAlert,
    showErrorSubmitAlert,
    openSubmitModal,
    setShowSavedAlert,
    setShowErrorSavedAlert,
    setShowErrorSubmitAlert,
    printTableContainer,
    hasPermissionToWrite,
    isMobile,
    isSaveSubmitEnabled,
    isEditMode,
    handleQuantityChange,
    isPrinting,
    handleConfirmSubmitModal,
    itemsPreviewOnSubmitModal,
    modifiedItems,
    blocker,
    setShowSubmitAlert,
  };

  const fireBaseProviderValue = useMemo(() => ({ passedValue }), [passedValue]);

  return (
    <WasteChecksheetDetailContext.Provider value={fireBaseProviderValue.passedValue}>
      {children}
    </WasteChecksheetDetailContext.Provider>
  );
};